import { Check } from 'lucide-react'
import type { Stage, AgentName } from '../../types'
import { STAGE_NAMES, AGENT_COLORS } from '../../types'

interface StageProgressBarProps {
  currentStage: Stage
  currentAgent: AgentName
}

const stages: Stage[] = [1, 2, 3, 4, 5]

/**
 * Survey strip: five numbered marks on a single rule. The current stage is the
 * only one carrying the agent's colour, so the eye lands on it without a pulse.
 */
export default function StageProgressBar({ currentStage, currentAgent }: StageProgressBarProps) {
  const agentColor = AGENT_COLORS[currentAgent]

  return (
    <div className="bg-surface border-b-2 border-navy px-4 py-2.5 shrink-0">
      <ol className="max-w-3xl mx-auto flex items-center gap-1 sm:gap-2">
        {stages.map((stage, i) => {
          const isCompleted = stage < currentStage
          const isCurrent = stage === currentStage

          return (
            <li key={stage} className="flex items-center flex-1 last:flex-initial">
              <div className="flex flex-col items-center gap-1">
                <div
                  className={`w-6 h-6 border-2 flex items-center justify-center mono-label leading-none ${
                    isCompleted
                      ? 'bg-navy border-navy text-stone'
                      : isCurrent
                        ? 'text-white'
                        : 'bg-paper border-navy/55 text-navy/70'
                  }`}
                  style={isCurrent ? { backgroundColor: agentColor, borderColor: agentColor } : undefined}
                  aria-current={isCurrent ? 'step' : undefined}
                >
                  {isCompleted ? <Check className="w-3.5 h-3.5" strokeWidth={3} /> : stage}
                </div>
                <span
                  className={`hidden sm:block mono-label text-[9px] tracking-[0.1em] leading-tight text-center max-w-[84px] ${
                    isCompleted ? 'text-navy' : isCurrent ? '' : 'text-navy/70'
                  }`}
                  style={isCurrent ? { color: agentColor } : undefined}
                >
                  {STAGE_NAMES[stage]}
                </span>
              </div>

              {i < stages.length - 1 && (
                <div
                  className={`flex-1 h-0.5 mx-1 sm:mx-2 ${
                    stage < currentStage ? 'bg-navy' : 'bg-navy/20'
                  }`}
                />
              )}
            </li>
          )
        })}
      </ol>
    </div>
  )
}
